import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../../api/client';

/* Platform (Abon-staff) gate — keys off `is_superuser`, never a role string
   or an email. Source of truth is the backend: the login/refresh payload now
   carries `is_superuser`. For a session issued before that shipped (no flag
   stored yet) we ask the backend ONCE via the superuser-only
   /billing/platform-summary/ (200 = superuser, 403 = not) and cache the answer
   for the session — so nobody is locked out mid-work and the answer is still
   the server's is_superuser check, not a client heuristic.
   Every /platform/* endpoint independently enforces is_superuser server-side;
   this is UX gating layered on a real control, not the control itself. */

const CACHE_KEY = 'platform_staff_probe';

export function readStoredIsSuperuser() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return typeof u.is_superuser === 'boolean' ? u.is_superuser : undefined;
  } catch { return undefined; }
}

export function useIsPlatformStaff() {
  const stored = readStoredIsSuperuser();
  const [probe, setProbe] = useState(() => {
    try { const v = sessionStorage.getItem(CACHE_KEY); return v == null ? undefined : v === '1'; }
    catch { return undefined; }
  });

  useEffect(() => {
    if (stored !== undefined || probe !== undefined) return undefined;
    let cancelled = false;
    api.get('/billing/platform-summary/')
      .then(() => { if (!cancelled) { setProbe(true); try { sessionStorage.setItem(CACHE_KEY, '1'); } catch {} } })
      .catch(() => { if (!cancelled) { setProbe(false); try { sessionStorage.setItem(CACHE_KEY, '0'); } catch {} } });
    return () => { cancelled = true; };
  }, [stored, probe]);

  if (stored !== undefined) return { isStaff: stored, resolved: true };
  return { isStaff: probe === true, resolved: probe !== undefined };
}

export default function RequireSuperuser({ children }) {
  const { isStaff, resolved } = useIsPlatformStaff();
  if (!resolved) return null; // at most one brief probe, for pre-flag sessions
  return isStaff ? children : <Navigate to="/dashboard" replace />;
}
